import {
  BasketItem,
  BASKET_STORAGE_KEY,
  findBasketItemByVariant,
  parseBasket,
  removeBasketItem,
  serializeBasket,
  upsertBasketItem,
} from 'pages/sales-lookup/utils/basket';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export interface BasketApi {
  items: BasketItem[];
  upsert: (item: BasketItem) => void;
  remove: (key: string) => void;
  clear: () => void;
  findByKey: (key: string) => BasketItem | undefined;
  findByVariant: (variantId: number) => BasketItem | undefined;
}

const BasketContext = createContext<BasketApi | null>(null);

// sessionStorage: survives the refresh/back-navigation a phone browser does
// mid-visit, dies with the tab so yesterday's basket never resurfaces. Reads
// and writes are guarded — private mode and some in-app browsers throw.
const readStorage = (): string | null => {
  try {
    return window.sessionStorage.getItem(BASKET_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStorage = (items: BasketItem[]) => {
  try {
    if (items.length) window.sessionStorage.setItem(BASKET_STORAGE_KEY, serializeBasket(items));
    else window.sessionStorage.removeItem(BASKET_STORAGE_KEY);
  } catch {
    // Persistence is a convenience; the in-memory basket still works.
  }
};

// Mounted once at the Sales Lookup page root so the basket outlives every
// screen transition inside the module.
export const BasketProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<BasketItem[]>(() => parseBasket(readStorage()));

  useEffect(() => {
    writeStorage(items);
  }, [items]);

  const upsert = useCallback((item: BasketItem) => setItems((current) => upsertBasketItem(current, item)), []);
  const remove = useCallback((key: string) => setItems((current) => removeBasketItem(current, key)), []);
  const clear = useCallback(() => setItems([]), []);

  const api = useMemo<BasketApi>(
    () => ({
      items,
      upsert,
      remove,
      clear,
      findByKey: (key) => items.find((item) => item.key === key),
      findByVariant: (variantId) => findBasketItemByVariant(items, variantId),
    }),
    [items, upsert, remove, clear]
  );

  return <BasketContext.Provider value={api}>{children}</BasketContext.Provider>;
};

export function useBasket(): BasketApi {
  const api = useContext(BasketContext);
  if (!api) throw new Error('useBasket must be used inside BasketProvider');
  return api;
}
